import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";
import { Link } from "../components/Link";

import { stalledTeachers, TeacherFunnel } from "./AdminTeacherActivityPage";

import type { ApiErrorCode } from "../api/client";
import type {
  AdminOverview,
  AdminTeacherActivity,
} from "@canvaschool/contracts";

const Card = ({
  title,
  rows,
}: {
  title: string;
  rows: Array<[string, number]>;
}) => (
  <section className="canvaschool-stat-card" aria-label={title}>
    <h2>{title}</h2>
    <dl>
      {rows.map(([label, value]) => (
        <div key={label}>
          <dt>{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  </section>
);

/** Numbers only: administrators never see class or canvas content. */
export const AdminDashboardPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [activity, setActivity] = useState<AdminTeacherActivity | null>(null);
  const [error, setError] = useState<ApiErrorCode | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [nextOverview, nextActivity] = await Promise.all([
        api.getAdminOverview(),
        api.getAdminTeacherActivity(),
      ]);
      setOverview(nextOverview);
      setActivity(nextActivity);
    } catch (failure) {
      setError(auth.handleError(failure));
    }
  }, [api, auth]);

  useEffect(() => {
    void load();
    // Loading once per mount; `auth` changes identity on every session update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const todo: Array<{ text: string; to: string }> = [];
  if (overview) {
    if (overview.beta.pending > 0) {
      todo.push({
        text: `대기 중인 베타 신청 ${overview.beta.pending}건`,
        to: "/admin/beta-applications?status=PENDING",
      });
    }
    if (overview.beta.credentialsUnsent > 0) {
      todo.push({
        text: `안내 메일을 보내지 못한 승인 ${overview.beta.credentialsUnsent}건`,
        to: "/admin/beta-applications?status=APPROVED",
      });
    }
    if (overview.teachers.initialPassword > 0) {
      todo.push({
        text: `초기 비밀번호를 쓰는 교사 ${overview.teachers.initialPassword}명`,
        to: "/admin/teachers",
      });
    }
  }
  const stalled = activity ? stalledTeachers(activity) : null;
  if (stalled && stalled.lateFirstLogin.length > 0) {
    todo.push({
      text: `안내 메일 후 3일 넘게 로그인하지 않은 교사 ${stalled.lateFirstLogin.length}명`,
      to: "/admin/teacher-activity",
    });
  }

  return (
    <section className="canvaschool-page">
      <div className="canvaschool-title-row">
        <div>
          <h1>대시보드</h1>
          <p className="canvaschool-page-lead">
            숫자만 보여 줍니다. 학급과 캔버스 내용은 표시되지 않습니다.
          </p>
        </div>
        <button type="button" onClick={() => void load()}>
          새로고침
        </button>
      </div>
      {error && <ErrorAlert code={error} />}
      {overview && (
        <>
          <section aria-label="처리할 일" className="canvaschool-todo">
            <h2>처리할 일</h2>
            {todo.length === 0 ? (
              <p className="canvaschool-hint">처리할 일이 없습니다.</p>
            ) : (
              <ul className="canvaschool-list">
                {todo.map((item) => (
                  <li key={item.to}>
                    <Link to={item.to}>{item.text}</Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section aria-label="실시간 관제" className="canvaschool-todo">
            <h2>실시간 관제</h2>
            <p className="canvaschool-hint">
              <Link to="/admin/live">지금 서버와 수업 상태 보기</Link>
            </p>
          </section>
          {activity && stalled && (
            <section aria-label="교사 이용 흐름" className="canvaschool-todo">
              <h2>교사 이용 흐름</h2>
              <TeacherFunnel activity={activity} />
              <p className="canvaschool-hint">
                첫 로그인 전(안내 메일 후 3일 넘음):{" "}
                {stalled.lateFirstLogin.join(", ") || "없음"}
              </p>
              <p className="canvaschool-hint">
                로그인했지만 학급 만들기 전:{" "}
                {stalled.noClass.join(", ") || "없음"}
              </p>
              <p className="canvaschool-hint">
                <Link to="/admin/teacher-activity">교사별 상세 보기</Link>
              </p>
            </section>
          )}
          <div className="canvaschool-stat-grid">
            <Card
              title="교사 계정"
              rows={[
                ["사용 중", overview.teachers.active],
                ["사용 중지", overview.teachers.disabled],
              ]}
            />
            <Card
              title="베타 신청"
              rows={[
                ["대기", overview.beta.pending],
                ["승인", overview.beta.approved],
                ["거절", overview.beta.rejected],
              ]}
            />
            <Card
              title="학급과 캔버스"
              rows={[
                ["운영 중 학급", overview.classes.active],
                ["보관된 학급", overview.classes.archived],
                ["학생", overview.students.enabled],
                ["캔버스", overview.canvases.active],
              ]}
            />
            <Card
              title="최근 7일 제보"
              rows={[
                ["오류", overview.reports7d.error],
                ["제보", overview.reports7d.userReport],
                ["설문", overview.reports7d.survey],
                ["진단", overview.reports7d.diagnostic],
              ]}
            />
          </div>
        </>
      )}
    </section>
  );
};
